// frontend/src/pages/AuctionLive.js
//
// UCC Super Series auction — part 2 screens (backend: services/auctionLive.js).
//
//   default export  AuctionScreen  — /auction/live (no login needed). The big
//                   screen at the venue, the owners' tablets, members and the
//                   public. Watch-only; reads /api/public/auction/live every
//                   second and redraws when the version changes.
//   AuctionControl  — /admin/auction/live/:id (super admin). The one screen
//                   that drives the auction: spin, start bid, team taps, undo,
//                   sold, Right To Match, unsold, re-open, rounds, allotment.
//
// Look follows the UCC site: light grey page, white cards, navy title bar with
// a gold rule, Rajdhani headings, navy/gold accents.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';

const NAVY = '#1E3A6E', NAVY_D = '#0F1E38', GOLD = '#F5C518', GOLD_D = '#C9940F', MUTED = '#6B7280', BORDER = '#E5E7EB';
const RAJ = 'Rajdhani, sans-serif';

export const money = (lakh) => (lakh == null ? '—' : lakh >= 100 ? `₹${(lakh / 100).toFixed(2)} Cr` : `₹${lakh} L`);
const dayShort = (d) => { const x = new Date(d + 'T00:00:00'); return `${x.getDate()} ${x.toLocaleString('en-IN', { month: 'short' })}`; };
const initials = (n) => String(n || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const ROLE = { batsman: 'Batsman', bowler: 'Bowler', keeper: 'Wicket keeper', allrounder: 'All-rounder' };
const BOWL = { medium: 'medium pace', fast: 'fast', off: 'off spin', leg: 'leg spin' };
function playStyle(c) {
  const parts = [];
  if (c.role) parts.push(ROLE[c.role]);
  if (c.bat_hand) parts.push(c.bat_hand);
  if (c.bowl_type && c.bowl_type !== 'none') parts.push(`${c.bowl_arm === 'left' ? 'left-arm' : 'right-arm'} ${BOWL[c.bowl_type]}`);
  return parts.join(' · ');
}

// Poll the live state; `version` decides whether to redraw.
function useLive(url, ms = 1000) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  const ver = useRef(-1);
  const load = useCallback(async () => {
    try {
      const r = await api.get(url);
      const v = r.data?.auction?.version ?? -2;
      if (v !== ver.current || !r.data?.auction) { ver.current = v; setSt(r.data); }
      setErr('');
    } catch (e) { setErr('Connection lost — retrying…'); }
  }, [url]);
  useEffect(() => { load(); const t = setInterval(load, ms); return () => clearInterval(t); }, [load, ms]);
  return [st, setSt, err, load];
}

// ── Pieces ────────────────────────────────────────────────────
function Avatar({ name, photo, size }) {
  return photo
    ? <img src={photo} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '4px solid white', boxShadow: '0 4px 14px rgba(0,0,0,.15)' }} />
    : <div style={{ width: size, height: size, borderRadius: '50%', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_D})`, color: NAVY_D,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: RAJ, fontWeight: 700, fontSize: size * 0.36,
                    border: '4px solid white', boxShadow: '0 4px 14px rgba(0,0,0,.15)' }}>{initials(name)}</div>;
}

function Dates({ dates, small }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
      {dates.map(d => (
        <span key={d.day} style={{ padding: small ? '2px 7px' : '4px 10px', borderRadius: 14, fontSize: small ? 11 : 13, fontWeight: 800,
          background: d.yes ? '#15803D' : '#E5E7EB', color: d.yes ? 'white' : MUTED }}>{dayShort(d.day)}</span>
      ))}
    </div>
  );
}

function Conditions({ c }) {
  if (!c.no_bowling && !c.no_keeping && !c.is_guest) return null;
  const pill = (t, bg, fg) => <span key={t} style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 800, background: bg, color: fg }}>{t}</span>;
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      {c.is_guest && pill('GUEST', '#FBF5DC', GOLD_D)}
      {c.no_bowling && pill('No bowling', '#FEF3C7', '#B45309')}
      {c.no_keeping && pill('No keeping', '#FEF3C7', '#B45309')}
    </div>
  );
}

function Stat({ v, l }) {
  return (
    <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center' }}>
      <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 24, color: NAVY, lineHeight: 1.1 }}>{v ?? '—'}</div>
      <div style={{ fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: '.06em' }}>{l}</div>
    </div>
  );
}

function FormBars({ last10 }) {
  const max = Math.max(50, ...last10.map(g => g.runs));
  if (!last10.length) return <div style={{ color: MUTED, fontSize: 13 }}>No UCC matches on record yet.</div>;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110, borderBottom: `1px solid ${BORDER}` }}>
        {last10.map((g, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: NAVY }}>{g.batted ? g.runs : '–'}{g.mom ? '★' : ''}</div>
            <div title={g.day} style={{ width: '100%', height: `${Math.max(3, (g.runs / max) * 85)}%`, borderRadius: '4px 4px 0 0',
              background: g.runs >= 50 ? GOLD : '#2E4D86' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
        {last10.map((g, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: g.wkts ? '#B91C1C' : MUTED, fontWeight: g.wkts ? 800 : 400 }}>
            {g.wkts ? `${g.wkts}w` : '·'}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlayerCard({ c }) {
  const s = c.stats || {};
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ width: 240, flex: '0 0 auto', textAlign: 'center', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}><Avatar name={c.name} photo={c.photo_url} size={170} /></div>
        <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 30, color: NAVY, marginTop: 10, lineHeight: 1.1 }}>{c.name}</div>
        <div style={{ fontSize: 13, color: MUTED, margin: '4px 0 10px' }}>{playStyle(c) || c.ucc_id}</div>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: MUTED, marginBottom: 4 }}>AVAILABLE</div>
        <Dates dates={c.dates} />
        <Conditions c={c} />
        {c.prev_team && <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>Last year: <b style={{ color: NAVY }}>{c.prev_team}</b></div>}
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: MUTED, marginTop: 12 }}>BASE PRICE</div>
        <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 28, color: GOLD_D }}>{money(c.base_lakh)}</div>
      </div>
      <div style={{ flex: 1, minWidth: 300 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: MUTED, marginBottom: 6 }}>CAREER AT UCC</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          <Stat v={s.matches} l="Matches" /><Stat v={s.runs?.toLocaleString('en-IN')} l="Runs" />
          <Stat v={s.bat_avg} l="Bat avg" /><Stat v={s.strike_rate} l="Strike rate" />
          <Stat v={`${s.hundreds || 0} / ${s.fifties || 0}`} l="100s / 50s" /><Stat v={s.wickets} l="Wickets" />
          <Stat v={s.economy} l="Economy" /><Stat v={s.catches} l="Catches" />
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: MUTED, margin: '16px 0 18px' }}>CURRENT FORM · LAST 10 GAMES</div>
        <FormBars last10={s.last10 || []} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
          <Stat v={s.last10_runs} l="Runs · last 10" /><Stat v={s.last10_wkts} l="Wkts · last 10" /><Stat v={s.last10_moms} l="MOM · last 10" />
        </div>
      </div>
    </div>
  );
}

function TeamStrip({ teams, holder, rtmTeam, compact }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${teams.length}, 1fr)`, gap: 10 }}>
      {teams.map(t => {
        const hold = t.id === holder;
        return (
          <div key={t.id} style={{ background: hold ? '#DBEAFE' : 'white', border: `1px solid ${hold ? t.color : BORDER}`, borderTop: `4px solid ${t.color || NAVY}`,
                                   borderRadius: 10, padding: compact ? '6px 10px' : '8px 12px', boxShadow: hold ? `0 0 0 2px ${t.color}` : 'none' }}>
            <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: compact ? 15 : 17, color: t.color || NAVY }}>
              {t.short_name || t.name}{hold ? ' ✋' : ''}{rtmTeam === t.id ? ' · RTM?' : ''}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: MUTED }}><span>Purse</span><b style={{ color: NAVY }}>{money(t.purse_left_lakh)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: MUTED }}><span>Squad</span><b style={{ color: NAVY }}>{t.squad_count}{t.places_left != null ? ` / ${t.squad_count + t.places_left}` : ''}</b></div>
            {!compact && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: MUTED, marginTop: 2 }}>
                <span>Max bid</span><b style={{ color: NAVY, fontFamily: RAJ, fontSize: 18 }}>{t.places_left ? money(t.max_bid_lakh) : 'Full'}</b>
              </div>
            )}
            <div style={{ fontSize: 10, color: MUTED }}>RTM {t.rtm_used ? 'used' : 'available'}</div>
          </div>
        );
      })}
    </div>
  );
}

function Squads({ teams }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(240px, 1fr))`, gap: 12 }}>
      {teams.map(t => (
        <div key={t.id} style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ background: t.color || NAVY, color: t.color === GOLD_D ? NAVY_D : 'white', padding: '8px 12px', fontFamily: RAJ, fontWeight: 700, fontSize: 17 }}>
            {t.name}
            <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.9 }}>Owner {t.owner_name || '—'} · {money(t.purse_left_lakh)} left</div>
          </div>
          {t.squad.map(p => (
            <div key={p.player_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 12px', borderBottom: `1px solid ${BORDER}`, fontSize: 13 }}>
              <span style={{ color: '#111827' }}>{p.name}{p.is_guest ? ' (G)' : ''}</span>
              <b style={{ color: NAVY }}>{p.status === 'owner' ? 'Owner' : p.status === 'retained' ? 'Retained' : money(p.price_lakh)}</b>
            </div>
          ))}
          {t.places_left > 0 && <div style={{ padding: '5px 12px', fontSize: 12, color: MUTED }}>{t.places_left} place{t.places_left === 1 ? '' : 's'} left</div>}
        </div>
      ))}
    </div>
  );
}

// The wheel: names round a disc; it spins and stops with the chosen name at
// the pointer. The server already chose — the animation only shows it.
const WHEEL_COLORS = [NAVY, GOLD, '#264d8c', GOLD_D, '#2E4D86', '#F5E6A3'];
function Wheel({ spin, size = 460, onDone }) {
  const [rot, setRot] = useState(0);
  const names = spin.names || [];
  const n = Math.max(1, names.length);
  const seg = 360 / n;
  useEffect(() => {
    const idx = Math.max(0, names.indexOf(spin.target));
    setRot(0);
    const t1 = setTimeout(() => setRot(360 * 6 + (360 - (idx + 0.5) * seg)), 50);
    const t2 = setTimeout(() => onDone && onDone(), 4700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [spin.id]); // eslint-disable-line
  const r = size / 2;
  return (
    <div style={{ position: 'relative', width: size, height: size + 14, margin: '0 auto' }}>
      <div style={{ position: 'absolute', left: '50%', top: 0, transform: 'translateX(-50%)', width: 0, height: 0, zIndex: 2,
                    borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderTop: '26px solid #B91C1C' }} />
      <svg width={size} height={size} viewBox={`${-r} ${-r} ${size} ${size}`} style={{ marginTop: 14,
           transform: `rotate(${rot}deg)`, transition: rot ? 'transform 4.4s cubic-bezier(.12,.6,.1,1)' : 'none' }}>
        {names.map((nm, i) => {
          const a0 = (i * seg - 90) * Math.PI / 180, a1 = ((i + 1) * seg - 90) * Math.PI / 180;
          const R = r - 4;
          const c = WHEEL_COLORS[i % WHEEL_COLORS.length];
          const dark = [GOLD, GOLD_D, '#F5E6A3'].includes(c);
          const mid = (i + 0.5) * seg - 90;
          return (
            <g key={i}>
              <path d={`M0,0 L${R * Math.cos(a0)},${R * Math.sin(a0)} A${R},${R} 0 ${seg > 180 ? 1 : 0},1 ${R * Math.cos(a1)},${R * Math.sin(a1)} z`} fill={c} stroke="white" strokeWidth="1.5" />
              <text transform={`rotate(${mid}) translate(${R - 10},0)`} textAnchor="end" dominantBaseline="middle"
                    style={{ fontFamily: RAJ, fontWeight: 700, fontSize: Math.min(16, Math.max(9, 300 / n)) }} fill={dark ? NAVY_D : 'white'}>
                {nm.length > 18 ? nm.slice(0, 17) + '…' : nm}
              </text>
            </g>
          );
        })}
        <circle r={r * 0.13} fill={NAVY_D} stroke={GOLD} strokeWidth="3" />
        <text textAnchor="middle" dominantBaseline="middle" fill={GOLD} style={{ fontFamily: RAJ, fontWeight: 700, fontSize: r * 0.09 }}>UCC</text>
      </svg>
    </div>
  );
}

// ── The big screen ────────────────────────────────────────────
export function AuctionStage({ st, compact }) {
  const a = st.auction, c = st.current;
  const [wheelDone, setWheelDone] = useState(true);
  const lastSpin = useRef(null);
  useEffect(() => {
    if (!a.spin?.id || a.spin.id === lastSpin.current) return;
    lastSpin.current = a.spin.id;
    // Only animate a fresh spin; a screen opened later goes straight to the card.
    const fresh = Date.now() - new Date(a.spin.at).getTime() < 6000;
    setWheelDone(!fresh);
  }, [a.spin]);
  const holder = c?.holder_team_id;
  const team = (id) => st.teams.find(t => t.id === id);
  const res = a.result;
  const spinning = !wheelDone && a.spin;

  let body;
  if (a.status === 'done' || a.stage === 'done') {
    const buys = st.teams.flatMap(t => t.squad.filter(p => p.status === 'sold').map(p => ({ ...p, team: t })))
      .sort((x, y) => (y.price_lakh || 0) - (x.price_lakh || 0)).slice(0, 5);
    body = (
      <>
        <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 30, color: NAVY, textAlign: 'center', marginBottom: 12 }}>🏆 Auction complete — the squads</div>
        {buys.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: MUTED, marginBottom: 6 }}>TOP BUYS</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {buys.map((p, i) => (
                <div key={p.player_id} style={{ flex: '1 1 160px', background: 'white', border: `1px solid ${BORDER}`, borderTop: `3px solid ${p.team.color || NAVY}`, borderRadius: 10, padding: '8px 10px' }}>
                  <div style={{ fontSize: 11, color: MUTED }}>#{i + 1} · {p.team.short_name || p.team.name}</div>
                  <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 17, color: NAVY }}>{p.name}</div>
                  <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 20, color: GOLD_D }}>{money(p.price_lakh)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Squads teams={st.teams} />
      </>
    );
  } else if (spinning) {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 30, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Wheel spin={a.spin} size={compact ? 300 : 460} onDone={() => setWheelDone(true)} />
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.12em', color: MUTED }}>{a.spin.kind === 'team' ? 'ALLOTTING' : 'SPINNING…'}</div>
          <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 38, color: NAVY, lineHeight: 1.05 }}>
            {a.spin.kind === 'team' ? <>Which team gets<br />{a.spin.player}?</> : <>Who's next<br />under the hammer?</>}
          </div>
          {a.spin.kind !== 'team' && <div style={{ color: MUTED, marginTop: 8 }}>Round {a.round} · {a.spin.left} left in this round</div>}
        </div>
      </div>
    );
  } else if (a.stage === 'card' && c) {
    body = <PlayerCard c={c} />;
  } else if ((a.stage === 'bidding' || a.stage === 'rtm') && c) {
    const ht = team(holder);
    const rt = team(a.rtm_team_id);
    body = (
      <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
        <div style={{ width: 210, textAlign: 'center', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}><Avatar name={c.name} photo={c.photo_url} size={120} /></div>
          <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 24, color: NAVY, marginTop: 6 }}>{c.name}</div>
          <div style={{ fontSize: 12, color: MUTED }}>{playStyle(c)}</div>
          <div style={{ fontSize: 12, color: MUTED, margin: '4px 0 8px' }}>{c.stats?.runs ?? 0} runs · {c.stats?.wickets ?? 0} wkts · form {c.stats?.last10_runs ?? 0} runs in last 10</div>
          <Dates dates={c.dates} small /><Conditions c={c} />
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ background: 'white', border: `2px solid ${ht?.color || BORDER}`, borderRadius: 12, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', color: MUTED }}>{ht ? 'CURRENT BID' : 'BIDDING OPENS AT'}</div>
              <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: compact ? 44 : 64, color: NAVY, lineHeight: 1 }}>{money(c.current_bid_lakh ?? c.next_bid_lakh)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.1em', color: MUTED }}>{ht ? 'HELD BY' : ''}</div>
              <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: compact ? 22 : 30, color: ht?.color || MUTED }}>{ht ? ht.name : 'No bid yet'}</div>
              {ht && <div style={{ fontSize: 13, color: MUTED }}>Next bid: {money(c.next_bid_lakh)}</div>}
            </div>
          </div>
          {a.stage === 'rtm' && rt && (
            <div style={{ marginTop: 10, background: '#FBF5DC', border: `2px solid ${GOLD_D}`, borderRadius: 12, padding: '10px 16px', fontFamily: RAJ, fontWeight: 700, fontSize: 24, color: NAVY }}>
              🃏 RIGHT TO MATCH? <span style={{ color: rt.color }}>{rt.name}</span> can match {money(c.current_bid_lakh)}
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: MUTED, margin: '10px 0 5px' }}>BIDS SO FAR</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', minHeight: 26 }}>
            {c.bids.map((b, i) => {
              const t = team(b.team_id);
              return <span key={i} style={{ background: t?.color || NAVY, color: t?.color === GOLD_D ? NAVY_D : 'white', borderRadius: 6, padding: '3px 9px', fontSize: 12, fontWeight: 700,
                                            outline: i === c.bids.length - 1 ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }}>{money(b.amount_lakh)} · {t?.short_name}</span>;
            })}
            {!c.bids.length && <span style={{ color: MUTED, fontSize: 13 }}>Waiting for the first bid at {money(c.next_bid_lakh)}…</span>}
          </div>
        </div>
      </div>
    );
  } else if ((a.stage === 'sold' || a.stage === 'unsold') && res) {
    const t = team(res.team_id);
    body = (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ display: 'inline-block', minWidth: 420, background: `linear-gradient(160deg, ${NAVY}, ${NAVY_D})`, border: `3px solid ${res.kind === 'unsold' ? MUTED : GOLD}`, borderRadius: 16, padding: '24px 40px', color: 'white' }}>
          <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: compact ? 44 : 72, letterSpacing: 3, color: res.kind === 'unsold' ? '#D1D5DB' : GOLD, lineHeight: 1 }}>
            {res.kind === 'unsold' ? 'UNSOLD' : res.kind === 'rtm' ? 'RTM!' : res.kind === 'allot' ? 'ALLOTTED' : 'SOLD!'}
          </div>
          <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 30, marginTop: 6 }}>{res.name}</div>
          {res.kind !== 'unsold'
            ? <div style={{ fontSize: 18, color: '#F5E6A3' }}>to {res.team_name} for <b>{money(res.price_lakh)}</b>{res.kind === 'rtm' ? ' (Right To Match)' : ''}</div>
            : <div style={{ fontSize: 16, color: '#D1D5DB' }}>{a.round < a.rounds_max ? `Back in round ${a.round + 1}` : 'To be allotted at the end'}</div>}
          {t && <div style={{ fontSize: 13, color: '#c7d2e8', marginTop: 6 }}>{t.short_name}: {money(t.purse_left_lakh)} left · {t.squad_count} / {t.squad_count + t.places_left}</div>}
        </div>
      </div>
    );
  } else {
    body = (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 40, color: NAVY }}>{a.status === 'live' ? `Round ${a.round}` : 'Auction'}</div>
        <div style={{ color: MUTED, fontSize: 16 }}>{st.counts.left_in_round} player{st.counts.left_in_round === 1 ? '' : 's'} left in this round · {st.counts.sold} sold · {st.counts.unsold} unsold</div>
        <div style={{ color: MUTED, fontSize: 14, marginTop: 4 }}>Next player coming up…</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, border: `1px solid ${BORDER}`, padding: compact ? 14 : 22, minHeight: compact ? 0 : 430, position: 'relative' }}>
        {a.paused && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,30,56,.75)', borderRadius: 14, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: GOLD, fontFamily: RAJ, fontWeight: 700, fontSize: 48 }}>⏸ PAUSED</div>
        )}
        {body}
      </div>
      {a.status !== 'done' && (
        <div style={{ marginTop: 12 }}>
          <TeamStrip teams={st.teams} holder={a.stage === 'bidding' || a.stage === 'rtm' ? holder : null} rtmTeam={a.stage === 'rtm' ? a.rtm_team_id : null} compact={compact} />
        </div>
      )}
    </div>
  );
}

export default function AuctionScreen() {
  const [st, , err] = useLive('/public/auction/live', 1000);
  const [tab, setTab] = useState('live');
  useEffect(() => { document.title = 'UCC Super Series — Auction'; }, []);
  const a = st?.auction;
  return (
    <div style={{ minHeight: '100vh', background: '#F5F6F8', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: NAVY, borderBottom: `3px solid ${GOLD}`, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontFamily: RAJ, fontWeight: 700, color: '#F5E6A3', letterSpacing: '.08em', fontSize: 16 }}>
          {st?.series?.name?.toUpperCase() || 'UCC SUPER SERIES'} · PLAYER AUCTION{a ? ` · ROUND ${a.round}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {a && ['live', 'squads'].map(k => (
            <button key={k} onClick={() => setTab(k)} style={{ border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontFamily: RAJ, fontWeight: 700,
              background: tab === k ? GOLD : 'transparent', color: tab === k ? NAVY_D : '#F5E6A3' }}>{k === 'live' ? 'Live' : 'Squads'}</button>
          ))}
          {a?.status === 'live' && <span style={{ background: '#B91C1C', color: 'white', fontWeight: 800, fontSize: 12, padding: '2px 8px', borderRadius: 4 }}>● LIVE</span>}
        </div>
      </div>
      <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
        {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 8 }}>{err}</div>}
        {!st && <div style={{ textAlign: 'center', color: MUTED, padding: 60 }}>Loading…</div>}
        {st && !a && <div style={{ textAlign: 'center', color: MUTED, padding: 60, fontSize: 18 }}>No auction is running right now.</div>}
        {a && tab === 'live' && <AuctionStage st={st} />}
        {a && tab === 'squads' && <Squads teams={st.teams} />}
      </div>
    </div>
  );
}

// ── Super admin control ───────────────────────────────────────
export function AuctionControl() {
  const { id } = useParams();
  const [st, setSt, err] = useLive(`/auction/${id}/live`, 1500);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function act(action, body, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true); setMsg('');
    try { const r = await api.post(`/auction/${id}/live/${action}`, body || {}); setSt(r.data); }
    catch (e) { setMsg(e?.response?.data?.error || e.message || 'That did not work'); }
    finally { setBusy(false); }
  }
  if (!st) return <div style={{ padding: 30, color: MUTED }}>{err || 'Loading…'}</div>;
  const a = st.auction, c = st.current;
  const team = (tid) => st.teams.find(t => t.id === tid);
  const holder = c?.holder_team_id;
  const B = (bg, fg = 'white') => ({ padding: '13px 10px', borderRadius: 10, border: 'none', background: bg, color: fg, fontFamily: RAJ, fontWeight: 700, fontSize: 17, cursor: 'pointer', width: '100%' });
  const blockStage = ['card', 'bidding', 'rtm'].includes(a.stage);
  const roundOver = st.counts.left_in_round === 0;

  return (
    <div style={{ background: '#F5F6F8', minHeight: '100%', paddingBottom: 30 }}>
      <div style={{ background: NAVY, borderBottom: `3px solid ${GOLD}`, color: 'white', padding: '10px 14px' }}>
        <div style={{ fontFamily: RAJ, fontWeight: 700, color: GOLD, fontSize: 12, letterSpacing: '.1em' }}>AUCTION CONTROL · {a.status.toUpperCase()} · ROUND {a.round}/{a.rounds_max}</div>
        <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 20 }}>{c && blockStage ? c.name : st.series?.name}</div>
        <div style={{ fontSize: 12, color: '#c7d2e8' }}>
          {st.counts.left_in_round} left in round · {st.counts.sold} sold · {st.counts.unsold} unsold · {st.counts.places_left} places to fill
        </div>
      </div>
      <div style={{ padding: 12, maxWidth: 560, margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, fontSize: 13 }}>
          <a href="/auction/live" target="_blank" rel="noreferrer" style={{ color: NAVY, fontWeight: 700 }}>📺 Open the big screen</a>
          <Link to="/admin/auction" style={{ color: MUTED, marginLeft: 'auto' }}>Setup</Link>
        </div>
        {msg && <div style={{ background: '#FEE2E2', color: '#B91C1C', padding: '9px 12px', borderRadius: 8, marginBottom: 10, fontSize: 14 }}>{msg}</div>}

        {a.status === 'setup' && (
          <>
            <button style={B('#15803D')} disabled={busy} onClick={() => act('start', null, 'Start the REAL auction?\n\nEveryone gets a notification that the auction is live. Rules, owners, retentions and the pool are locked.')}>▶ Start auction (notify everyone)</button>
            <button style={{ ...B('white', NAVY), marginTop: 8 }} disabled={busy} onClick={() => act('start', { rehearsal: true }, 'Start a REHEARSAL?\n\nNo notifications are sent. Afterwards press Reset auction on the setup page.')}>Start rehearsal (no notifications)</button>
          </>
        )}

        {a.status === 'live' && (
          <>
            {/* The player on the block */}
            {c && blockStage && (
              <div style={{ background: 'white', border: `1px solid ${BORDER}`, borderRadius: 12, padding: 12, marginBottom: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: MUTED }}>{a.stage === 'card' ? 'On screen — player card' : a.stage === 'rtm' ? 'Right To Match' : 'Bidding'}</div>
                <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 40, color: NAVY, lineHeight: 1 }}>{money(c.current_bid_lakh)}</div>
                <div style={{ fontWeight: 700, color: team(holder)?.color || MUTED }}>{team(holder)?.name || 'No bid yet'}</div>
              </div>
            )}

            {a.stage === 'card' && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                <button style={B(NAVY)} disabled={busy} onClick={() => act('start_bid')}>Start bid · {money(c?.next_bid_lakh)}</button>
                <button style={B('#6B7280')} disabled={busy} onClick={() => act('unsold', null, `${c?.name} unsold?`)}>Unsold</button>
              </div>
            )}

            {a.stage === 'bidding' && c && (
              <>
                <div style={{ fontSize: 12, color: MUTED, textAlign: 'center', margin: '4px 0 6px' }}>TAP THE TEAM THAT CALLED · next bid {money(c.next_bid_lakh)}</div>
                {st.teams.map(t => {
                  const isHolder = t.id === holder;
                  const cant = isHolder || !t.places_left || c.next_bid_lakh > t.max_bid_lakh;
                  return (
                    <button key={t.id} disabled={busy || cant || a.paused} onClick={() => act('bid', { team_id: t.id })}
                            style={{ ...B(cant ? '#E5E7EB' : t.color || NAVY, cant ? MUTED : (t.color === GOLD_D ? NAVY_D : 'white')), marginBottom: 7, cursor: cant ? 'default' : 'pointer' }}>
                      {t.short_name || t.name} {isHolder ? '(holds the bid)' : !t.places_left ? '— squad full' : c.next_bid_lakh > t.max_bid_lakh ? `— max ${money(t.max_bid_lakh)}` : `→ ${money(c.next_bid_lakh)}`}
                    </button>
                  );
                })}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
                  <button style={B('white', NAVY)} disabled={busy || !c.bids.length} onClick={() => act('undo')}>↶ Undo last bid</button>
                  <button style={B('white', NAVY)} disabled={busy} onClick={() => act('pause', { paused: !a.paused })}>{a.paused ? '▶ Resume' : '⏸ Pause'}</button>
                  {c.bids.length
                    ? <button style={{ ...B('#15803D'), gridColumn: 'span 2' }} disabled={busy}
                              onClick={() => act('sold', null, `Sold ${c.name} to ${team(holder)?.name} for ${money(c.current_bid_lakh)}?`)}>SOLD · {money(c.current_bid_lakh)} → {team(holder)?.short_name}</button>
                    : <button style={{ ...B('#6B7280'), gridColumn: 'span 2' }} disabled={busy} onClick={() => act('unsold', null, `${c.name} unsold?`)}>Unsold (no bids)</button>}
                </div>
              </>
            )}

            {a.stage === 'rtm' && c && (
              <div style={{ background: '#FBF5DC', border: `2px solid ${GOLD_D}`, borderRadius: 12, padding: 12 }}>
                <div style={{ fontFamily: RAJ, fontWeight: 700, fontSize: 18, color: NAVY, marginBottom: 8 }}>
                  🃏 Does {team(a.rtm_team_id)?.name} use its Right To Match at {money(c.current_bid_lakh)}?
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button style={B(team(a.rtm_team_id)?.color || NAVY)} disabled={busy} onClick={() => act('rtm', { use: true })}>Use RTM</button>
                  <button style={B('#6B7280')} disabled={busy} onClick={() => act('rtm', { use: false })}>No — sold to {team(holder)?.short_name}</button>
                </div>
                <button style={{ ...B('white', NAVY), marginTop: 8 }} disabled={busy} onClick={() => act('undo')}>↶ Back to bidding</button>
              </div>
            )}

            {!blockStage && (
              <>
                {(a.stage === 'sold' || a.stage === 'unsold') && a.result && a.result.kind !== 'allot' && (
                  <button style={{ ...B('white', '#B91C1C'), marginBottom: 8 }} disabled={busy}
                          onClick={() => act('reopen', null, `Re-open ${a.result.name}? ${a.result.kind === 'unsold' ? '' : `${money(a.result.price_lakh)} goes back to ${a.result.team_short}'s purse and `}bidding continues.`)}>
                    ↺ Re-open {a.result.name}
                  </button>
                )}
                {!roundOver && st.counts.places_left > 0 && (
                  <button style={B(GOLD, NAVY_D)} disabled={busy || a.paused} onClick={() => act('spin')}>🎡 Spin the wheel</button>
                )}
                {roundOver && a.round < a.rounds_max && st.counts.remaining > 0 && st.counts.places_left > 0 && (
                  <button style={B(NAVY)} disabled={busy} onClick={() => act('next_round', null, `Start round ${a.round + 1} with the ${st.counts.remaining} unsold players?`)}>
                    ▶ Start round {a.round + 1} ({st.counts.remaining} unsold)
                  </button>
                )}
                {roundOver && a.round >= a.rounds_max && st.counts.remaining > 0 && st.counts.places_left > 0 && (
                  <button style={B(NAVY)} disabled={busy} onClick={() => act('allot')}>🎯 Allot next player at {money(a.base_lakh)}</button>
                )}
                {st.counts.places_left === 0 && (
                  <button style={B('#15803D')} disabled={busy} onClick={() => act('finish', null, 'Finish the auction? The squads are final.')}>🏁 Finish auction</button>
                )}
                <button style={{ ...B('white', NAVY), marginTop: 8 }} disabled={busy} onClick={() => act('pause', { paused: !a.paused })}>{a.paused ? '▶ Resume' : '⏸ Pause screens'}</button>
              </>
            )}
          </>
        )}

        <div style={{ marginTop: 16 }}><TeamStrip teams={st.teams} holder={a.stage === 'bidding' ? holder : null} rtmTeam={a.stage === 'rtm' ? a.rtm_team_id : null} compact /></div>
        {a.status === 'done' && <div style={{ marginTop: 12 }}><Squads teams={st.teams} /></div>}
      </div>
    </div>
  );
}
